import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSessionContext } from "./SessionContext";
import { PatientBasedAppointment } from "../services/fhir";
import { useLocationContext } from "./LocationContext";

interface PatientOrganizationContextValue {
  patients: fhir5.BundleEntry<fhir5.Patient>[] | undefined;
  refetchPatientOrganization: Function;
  patientOrganizationLoading: boolean;
}

export const PatientOrganizationContext =
  createContext<PatientOrganizationContextValue>(null!);

export function PatientOrganizationContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { access, user } = useSessionContext();
  const { location, locationLoading } = useLocationContext();
  const [patients, setPatients] =
    useState<fhir5.BundleEntry<fhir5.Patient>[]>();
  const [refetch, setRefetch] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const lastRequestIdRef = useRef(0);
  const lastOrgIdRef = useRef<string | undefined>(undefined);

  function refetchPatientOrganization() {
    setRefetch(!refetch);
  }

  useEffect(() => {
    /**
     * Segurança: ao trocar de organização, limpar imediatamente pacientes para
     * evitar qualquer "vazamento visual" de dados da org anterior.
     */
    if (access?.type === "professional") {
      const orgId = access?.organizationId;
      if (lastOrgIdRef.current !== orgId) {
        lastOrgIdRef.current = orgId;
        setPatients(undefined);
        setLoading(true);
      }
    }

    async function fetchPatientOrganization() {
      const locationId = location
        ?.map(
          (element: fhir5.BundleEntry<fhir5.Location>) => element.resource?.id
        )
        ?.join(",");

      try {
        // const summary = await PatientBasedAppointment({
        //   locationActors: locationId,
        //   summary: true,
        // });

        const patientResponse = await PatientBasedAppointment({
          locationActors: locationId,
          count: 20000,
        });
        return patientResponse?.entry || [];
      } catch (err) {
        console.log("PatientOrganization", err);
        return [];
      } finally {
      }
    }

    const shouldHandleProfessional =
      access.type === "professional" && !locationLoading;

    // Importante: não deixar loading infinito quando não há locations.
    if (!shouldHandleProfessional) {
      setLoading(false);
      return;
    }

    const hasLocations = Array.isArray(location) && location.length > 0;
    if (!hasLocations) {
      setPatients([]);
      setLoading(false);
      return;
    }

    /**
     * Race guard: se trocar organização rapidamente (A -> B -> A),
     * uma resposta antiga não pode sobrescrever o estado da última seleção.
     */
    const requestId = ++lastRequestIdRef.current;
    setLoading(true);
    (async () => {
      const nextPatients = await fetchPatientOrganization();
      if (lastRequestIdRef.current !== requestId) return;
      setPatients(nextPatients);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access?.organizationId, refetch, locationLoading, location]);

  const contextValue = {
    patients,
    refetchPatientOrganization,
    patientOrganizationLoading: loading,
  };

  return (
    <PatientOrganizationContext.Provider value={contextValue}>
      {children}
    </PatientOrganizationContext.Provider>
  );
}

export function usePatientOrganizationContext() {
  return useContext(PatientOrganizationContext);
}
